/**
 * 投资活动纯逻辑（无 React / 无 DB / 无 DOM 字符串）
 * 状态与排序按当天日期实时计算；卡片与筛选区块已改为真正的 JSX 组件。
 */
import type {InvestOpportunity, InvestOpportunityType} from '@/common/interface/invest';
import {INVEST_TYPE_LABEL} from '@/common/interface/invest';

/** 类型行与地区行的「全部」tab 文案 */
export const INVEST_ALL_TYPE = '全部机会';
export const INVEST_ALL_REGION = '全部地区';

/** 本地日期 → YYYY-MM-DD（避免 toISOString 的时区偏移，与活动模块一致） */
export function ymd(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 报名状态：已截止 / 报名中 / 持续更新，按当天日期实时计算 */
export function statusOf(item: InvestOpportunity, now = new Date()): {text: string; ended: boolean} {
    if (!item.deadline || item.rolling) return {text: '持续更新', ended: false};
    if (!/^\d{4}-\d{2}-\d{2}$/.test(item.deadline)) return {text: '持续更新', ended: false};
    return item.deadline < ymd(now) ? {text: '已截止', ended: true} : {text: '报名中', ended: false};
}

/** 类型 tab：按数据里出现过的类型给出，保持 INVEST_TYPE_LABEL 的声明顺序 */
export function typeLabels(list: InvestOpportunity[]): string[] {
    const used = new Set(list.map((i) => i.type));
    const order = Object.keys(INVEST_TYPE_LABEL) as InvestOpportunityType[];
    return [INVEST_ALL_TYPE, ...order.filter((t) => used.has(t)).map((t) => INVEST_TYPE_LABEL[t])];
}

/** 地区 tab：「全国」始终排在具体地区之前，其余按条数倒序 */
export function regionLabels(list: InvestOpportunity[]): string[] {
    const count = new Map<string, number>();
    list.forEach((i) => {
        if (i.region) count.set(i.region, (count.get(i.region) || 0) + 1);
    });
    const rest = [...count.entries()]
        .filter(([r]) => r !== '全国')
        .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0], 'zh-Hans-CN'))
        .map(([r]) => r);
    return [INVEST_ALL_REGION, ...(count.has('全国') ? ['全国'] : []), ...rest];
}

/** 机会条数文案，对应页面 .opportunity-title 里的计数 */
export function investCountText(list: InvestOpportunity[]): string {
    return `${list.length} 条信息`;
}

/** 报名中优先 → 持续更新 → 已截止；同组按发布时间倒序。随当天日期变化 */
export function sortInvest(list: InvestOpportunity[], now = new Date()): InvestOpportunity[] {
    const rank = (i: InvestOpportunity) => {
        const s = statusOf(i, now);
        return s.ended ? 2 : (s.text === '报名中' ? 0 : 1);
    };
    return [...list].sort((a, b) => (
        rank(a) - rank(b) || (a.publishDate === b.publishDate ? 0 : (a.publishDate < b.publishDate ? 1 : -1))
    ));
}
